import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { getCurrentToken, getAccHostname } from '@adsk/acs-sc-web-platform-api';
import { Flex } from '@adsk/alloy-react-box';
import { BasicButton } from '@adsk/alloy-react-button';
import Divider from '@adsk/alloy-react-divider';
import Illustration from '@adsk/alloy-react-illustration';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import { useSideNavigation } from '@adsk/alloy-side-navigation';
import TrialBanner from '@adsk/alloy-trial-banner';
import { accountsListShape, productsListShape, projectShape, userShape } from './helpers/propTypes';
import EnvLabel from './layout/EnvLabel';
import Header from './layout/Header';
import ProductButton from './layout/ProductButton';
import ProfileButton from './layout/ProfileButton';
import ProjectButton from './layout/ProjectButton';
import QuestionMarkMenu from './layout/QuestionMarkMenu';
import TopBar from './layout/TopBar';
/**
 * Acs Unified TopNavigation
 */
const TopNavigation = props => {
  const {
    style,
    className,
    logo,
    login,
    admin,
    showHelpMenu,
    projectId,
    accountId,
    currentProductId,
    currentProject,
    accounts,
    products,
    user,
    onLogout,
    helpLinks,
    showTrialBanner,
    showSubscriptionsModal,
    hideContactSupportFromHelpMenu,
    isProductRequired,
    renderCustomProductHeader,
    onProductClick,
    onProjectClick,
    onAccountClick,
    UNSAFE_showBuildersUserProfileModal,
    UNSAFE_isBuildersProfileModalEnabled
  } = props;
  const {
    T
  } = useLocale(_ref => {
    let {
      locale
    } = _ref;
    return require(`./i18n/${locale}.json`);
  });
  const {
    collapsed
  } = useSideNavigation();
  const onLogoutWithSolvvy = useCallback(() => {
    var _solvvyApi$clearSolvv;
    const {
      solvvyApi
    } = window;
    solvvyApi === null || solvvyApi === void 0 ? void 0 : (_solvvyApi$clearSolvv = solvvyApi.clearSolvvyState) === null || _solvvyApi$clearSolvv === void 0 ? void 0 : _solvvyApi$clearSolvv.call(solvvyApi);
    onLogout === null || onLogout === void 0 ? void 0 : onLogout();
  }, [onLogout]);
  const userHomeUrl = getAccHostname();
  return /*#__PURE__*/React.createElement(Flex, {
    className: className,
    flexDirection: "column",
    width: "100%",
    style: style
  }, /*#__PURE__*/React.createElement(TopBar, {
    hideLogo: logo,
    admin: admin
  }), /*#__PURE__*/React.createElement(EnvLabel, null), /*#__PURE__*/React.createElement(Header, {
    left: /*#__PURE__*/React.createElement(React.Fragment, null, logo && /*#__PURE__*/React.createElement("a", {
      "data-testid": "TopNavigation__Header__Logo__a",
      href: userHomeUrl
    }, /*#__PURE__*/React.createElement(Illustration, {
      type: Illustration.TYPES.ACC_TM_FULL_SINGLE_NEW_2022_BLACK,
      style: {
        verticalAlign: 'middle',
        margin: '0 16px'
      }
    })), 'products' in props ? /*#__PURE__*/React.createElement(ProductButton, {
      productId: currentProductId,
      products: products,
      required: isProductRequired,
      renderCustomHeader: renderCustomProductHeader,
      onProductClick: onProductClick
    }) : null, !collapsed && 'products' in props && /*#__PURE__*/React.createElement(Divider, {
      color: collapsed ? theme.colors.white : undefined,
      variant: Divider.VARIANTS.VERTICAL,
      style: {
        display: 'inline-block',
        height: 32,
        margin: '0 16px',
        [theme.mediaQuery.small]: {
          display: 'none'
        }
      }
    }), 'accounts' in props || 'currentProject' in props ? /*#__PURE__*/React.createElement(ProjectButton, {
      accountId: accountId,
      projectId: projectId,
      productId: currentProductId,
      currentProject: currentProject,
      accounts: accounts,
      onProjectClick: onProjectClick,
      onAccountClick: onAccountClick
    }) : null),
    right: /*#__PURE__*/React.createElement(React.Fragment, null, showTrialBanner && /*#__PURE__*/React.createElement(TrialBanner, {
      user: user,
      currentProductId: currentProductId
    }), showHelpMenu && /*#__PURE__*/React.createElement(QuestionMarkMenu, {
      helpLinks: helpLinks,
      currentProductId: currentProductId,
      isProductRequired: isProductRequired,
      hideContactSupport: hideContactSupportFromHelpMenu
    }), (showHelpMenu || showTrialBanner) && /*#__PURE__*/React.createElement(Divider, {
      variant: Divider.VARIANTS.VERTICAL,
      style: {
        height: 32,
        margin: '0 16px'
      }
    }), 'user' in props && /*#__PURE__*/React.createElement(ProfileButton, {
      user: user,
      projectId: projectId,
      onLogout: onLogoutWithSolvvy,
      showSubscriptionsModal: showSubscriptionsModal,
      UNSAFE_showBuildersUserProfileModal: UNSAFE_showBuildersUserProfileModal,
      UNSAFE_isBuildersProfileModalEnabled: UNSAFE_isBuildersProfileModalEnabled
    }), login && !('user' in props) && /*#__PURE__*/React.createElement(BasicButton, {
      style: theme.typography.heading4,
      onClick: () => getCurrentToken()
    }, T.translate('SIGN_IN')))
  }));
};
TopNavigation.displayName = 'TopNavigation';
TopNavigation.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** list of current user all projects */
  accounts: accountsListShape,
  /** list of products a user could move across */
  products: productsListShape,
  /** The current account ID, to mark as selected in the ProjectPicker */
  accountId: PropTypes.string,
  /** The current project, to mark as selected in the ProjectPicker */
  currentProject: projectShape,
  /** The current product ID, to mark as selected in the ProductPicker */
  currentProductId: PropTypes.string,
  /** current projectId of project or template */
  projectId: PropTypes.string,
  /** current user account details */
  user: userShape,
  /** display help menu */
  showHelpMenu: PropTypes.bool,
  /** show subscriptions modal (page level) */
  showSubscriptionsModal: PropTypes.bool,
  /** mode for admin pages */
  admin: PropTypes.bool,
  /** display logo */
  logo: PropTypes.bool,
  /** display login button */
  login: PropTypes.bool,
  /** callback called on logout */
  onLogout: PropTypes.func,
  /** Override default help links */
  helpLinks: PropTypes.shape({
    help: PropTypes.string,
    about: PropTypes.string
  }),
  /** display trial banner */
  showTrialBanner: PropTypes.bool,
  /** is product required */
  isProductRequired: PropTypes.bool,
  /** Render custom product picker header */
  renderCustomProductHeader: PropTypes.func,
  /** Custom product click handler */
  onProductClick: PropTypes.func,
  /** Custom project click handler */
  onProjectClick: PropTypes.func,
  /** Custom account click handler */
  onAccountClick: PropTypes.func,
  /** Triggers opening the Builders Network Profile Modal */
  UNSAFE_showBuildersUserProfileModal: PropTypes.func,
  /** When BuildersNetworkProfile is enabled, it updates the TopNav Profile dropdown options to include button for opening the BN Modal and hides the Profile settings button*/
  UNSAFE_isBuildersProfileModalEnabled: PropTypes.bool
};
export default TopNavigation;
//# sourceMappingURL=TopNavigation.js.map